import type {
  AudienceRevisionSnapshot,
  AudienceType,
  CampaignAudienceSegment,
  RuleGroup,
} from '@/api/types';

type SegmentRevisionKey = Pick<
  CampaignAudienceSegment,
  'audience_id' | 'audience_revision'
>;

/** The parts of a pinned snapshot a definition is rebuilt from. */
export type AudienceRevisionDefinition = Pick<
  AudienceRevisionSnapshot,
  'audience_id' | 'revision' | 'name' | 'description' | 'audience_type' | 'rules'
>;

export interface CurrentAudienceDefinition {
  id: string;
  name: string;
  description: string | null;
  audience_type: AudienceType;
  rules: RuleGroup;
}

export interface ResolvedAudienceDefinition extends CurrentAudienceDefinition {
  pinned_revision?: number;
}

export interface PreflightCheckLike {
  code: string;
  status: string;
}

export interface AudienceRepinStrategy {
  forceSave: boolean;
  forceAudienceFork: boolean;
}

/**
 * A direct revision drift is repaired by saving the visual definition into a
 * new campaign revision. A transitive seed drift additionally needs a new
 * audience snapshot because seed pins belong to the immutable audience
 * revision itself.
 */
export function audienceRepinStrategy(
  checks: readonly PreflightCheckLike[] = [],
): AudienceRepinStrategy {
  const directDrift = checks.some(
    (check) =>
      check.code === 'audience_revision' && check.status === 'fail',
  );
  const seedDrift = checks.some(
    (check) =>
      check.code === 'audience_seed_revisions' && check.status === 'fail',
  );
  return {
    forceSave: directDrift || seedDrift,
    forceAudienceFork: seedDrift,
  };
}

/**
 * Resolve a campaign segment from its immutable snapshot first.
 *
 * The mutable audience projection is only a compatibility fallback for older
 * API responses that predate hydrated campaign plans.
 */
export function resolveCampaignAudienceDefinition(
  segment: SegmentRevisionKey,
  snapshots: readonly AudienceRevisionDefinition[] = [],
  currentAudiences: readonly CurrentAudienceDefinition[] = [],
): ResolvedAudienceDefinition | undefined {
  const snapshot = snapshots.find(
    (candidate) =>
      candidate.audience_id === segment.audience_id
      && candidate.revision === segment.audience_revision,
  );
  if (snapshot) {
    return {
      id: snapshot.audience_id,
      name: snapshot.name,
      description: snapshot.description,
      audience_type: snapshot.audience_type,
      rules: snapshot.rules,
      pinned_revision: snapshot.revision,
    };
  }

  return currentAudiences.find(
    (candidate) => candidate.id === segment.audience_id,
  );
}
